// Chèn emoji vào ô soạn tin tại đúng chỗ con trỏ.
//
// Tách khỏi ChatThread.tsx để ô soạn tin giữ nguyên độ dài cũ, và để chỗ
// chèn có bài kiểm riêng — đây là phần dễ sai nhất: con trỏ của một
// controlled input chỉ đặt lại được SAU khi React vẽ xong.
import { useEffect, useRef } from "react";

/**
 * Trần chữ của ô soạn tin, khớp `MaxBodyChars` bên máy chủ.
 *
 * Máy chủ đếm RUNE còn ô nhập đếm đơn vị UTF-16, mà emoji thường chiếm hai
 * đơn vị — nên phía này luôn chặt hơn hoặc bằng máy chủ. Chặt hơn thì người
 * gõ biết ngay lúc gõ; lỏng hơn mới là cú gửi rồi ăn lỗi từ máy chủ.
 */
export const MAX_BODY_CHARS = 4000;

/** `E`: ô một dòng (<input>, cổng khách + mục đối tác) hay nhiều dòng (<textarea>, ô soạn nhân viên). */
export function useEmojiInsert<E extends HTMLInputElement | HTMLTextAreaElement = HTMLInputElement>(
  text: string,
  setText: (next: string) => void,
) {
  const textRef = useRef<E | null>(null);
  // Vị trí con trỏ cần đặt lại sau khi chèn. Giữ trong ref rồi đặt ở effect,
  // không đặt ngay lúc bấm: ô là controlled input nên DOM chỉ mang chữ mới
  // SAU khi React vẽ lại, đặt sớm là con trỏ nhảy về chỗ cũ. Ref chứ không
  // state — dọn cờ bằng state trong effect là một vòng vẽ lại thừa.
  const caretRef = useRef<number | null>(null);

  useEffect(() => {
    const pos = caretRef.current;
    if (pos === null) return;
    caretRef.current = null;
    const el = textRef.current;
    if (!el) return;
    el.focus();
    el.setSelectionRange(pos, pos);
  }, [text]);

  /**
   * Chèn emoji tại đúng chỗ con trỏ đang đứng, thay phần đang bôi đen.
   *
   * Nối vào cuối chuỗi thì người gõ nửa câu rồi chọn icon sẽ thấy icon nhảy
   * xuống cuối — chính cú làm họ phải xoá đi gõ lại.
   */
  const insertEmoji = (ch: string) => {
    const el = textRef.current;
    const start = el?.selectionStart ?? text.length;
    const end = el?.selectionEnd ?? text.length;
    const next = text.slice(0, start) + ch + text.slice(end);
    if (next.length > MAX_BODY_CHARS) return;
    caretRef.current = start + ch.length;
    setText(next);
  };

  return { textRef, insertEmoji };
}
